// src/components/ImageManager.js
import React, { useState, useMemo } from 'react';
import './ImageManager.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faStar, faTrashAlt, faUndo, faRedo } from '@fortawesome/free-solid-svg-icons';
import { getFullImageUrl } from '../utils/config';

const ImageManager = ({
  propertyId,
  mainImageURL,
  propertyImages,
  stagedImages = [], // New prop for staged images
  onImageUpload,
  onImageDelete,
  onDeleteStagedImage, // New prop for deleting staged images
  onSetMainImage,
  onSetMainStagedImage, // New prop for setting staged as main
  onImageRotate,
  isReadOnly,
  showAppNotification,
  uploadingFiles = [],
}) => {
  const [isDraggingOverMain, setIsDraggingOverMain] = useState(false);
  const [isDraggingOverAdditional, setIsDraggingOverAdditional] = useState(false);

  // This function is now simplified. It just calls the onImageUpload prop.
  // The parent component will decide whether to stage or upload directly.
  const processFiles = (selectedFiles, isMain) => {
    console.log('ImageManager: processFiles called', selectedFiles, isMain);
    if (selectedFiles && selectedFiles.length > 0) {
      for (let i = 0; i < selectedFiles.length; i++) {
        const tempId = `upload-${Date.now()}-${i}`; // Generate a temporary client-side ID
        onImageUpload(selectedFiles[i], isMain, tempId); // Pass file and tempId up to parent
      }
    }
  };

  const handleFileSelect = (event, isMain) => {
    processFiles(event.target.files, isMain);
    event.target.value = null; // Reset file input to allow selecting the same file again
  };

  // Combined logic for displaying main image from either saved URL or staged images
  const mainImage = useMemo(() => {
    if (mainImageURL) {
      return { type: 'saved', url: mainImageURL, id: mainImageURL };
    }
    const mainStaged = stagedImages.find((img) => img.isMain);
    if (mainStaged) {
      return { type: 'staged', url: mainStaged.localUrl, id: mainStaged.tempId };
    }
    return null;
  }, [mainImageURL, stagedImages]);

  // Combined logic for displaying additional images
  const additionalImages = useMemo(() => {
    const saved = (propertyImages || []).map((url) => ({ type: 'saved', url, id: url }));
    const staged = stagedImages.filter((img) => !img.isMain).map((img) => ({ type: 'staged', url: img.localUrl, id: img.tempId }));
    return [...saved, ...staged];
  }, [propertyImages, stagedImages]);

  const handleDeleteClick = (image) => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      if (image.type === 'saved') {
        onImageDelete(image.id, image.id === mainImageURL);
      } else {
        onDeleteStagedImage(image.id);
      }
    }
  };

  const handleSetMainClick = (image) => {
    if (image.type === 'saved') {
      onSetMainImage(image.id);
    } else {
      onSetMainStagedImage(image.id);
    }
  };

  // Drag and Drop Handlers
  const handleDragEnter = (e, areaSetter) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isReadOnly) areaSetter(true);
  };

  const handleDragLeave = (e, areaSetter) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isReadOnly) areaSetter(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // You can add visual cues here if needed, e.g., e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = (e, areaSetter, isMain) => {
    e.preventDefault();
    e.stopPropagation();
    if (isReadOnly) return;

    areaSetter(false);
    const files = e.dataTransfer.files;
    processFiles(files, isMain);
  };

  // Filter uploading files for main and additional sections
  const uploadingMainFile = uploadingFiles.find((f) => f.isMain && (f.status === 'uploading' || f.status === 'processing'));
  const uploadingAdditionalFiles = uploadingFiles.filter((f) => !f.isMain && (f.status === 'uploading' || f.status === 'processing'));

  return (
    <div className="image-manager-container">
      <div
        className={`photos-section main-image-display-section ${isDraggingOverMain ? 'dragging-over' : ''}`}
        onDragEnter={(e) => handleDragEnter(e, setIsDraggingOverMain)}
        onDragLeave={(e) => handleDragLeave(e, setIsDraggingOverMain)}
        onDragOver={handleDragOver}
        onDrop={(e) => handleDrop(e, setIsDraggingOverMain, true)}
      >
        <div className="main-image-content-wrapper">
          {' '}
          {/* New wrapper */}
          <h4>Main Property Image</h4>
          <div className="main-image-area">
            {' '}
            {/* New wrapper for image/upload area */}
            {mainImage ? (
              <div className="image-item main-image-item">
                <img src={mainImage.type === 'saved' ? getFullImageUrl(mainImage.url) : mainImage.url} alt="Main property view" className="image-thumbnail main-image-thumbnail" />
                {!isReadOnly && (
                  <div className="image-actions">
                    {/* Rotation is now available for both saved and staged images */}
                    <button onClick={() => onImageRotate(mainImage, 'left')} className="rotate-image-btn" title="Rotate Left">
                      <FontAwesomeIcon icon={faUndo} />
                    </button>
                    <button onClick={() => onImageRotate(mainImage, 'right')} className="rotate-image-btn" title="Rotate Right">
                      <FontAwesomeIcon icon={faRedo} />
                    </button>
                    <button onClick={() => handleDeleteClick(mainImage)} className="delete-image-btn" title="Delete Main Image">
                      <FontAwesomeIcon icon={faTrashAlt} />
                    </button>
                  </div>
                )}
              </div>
            ) : uploadingMainFile ? (
              // This part for uploading placeholder remains the same
              <div className="image-item uploading-item main-image-uploading-placeholder" style={{ width: '300px', height: 'auto', padding: '20px', justifyContent: 'center' }}>
                {uploadingMainFile.status === 'processing' ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                    <p style={{ marginTop: '10px' }}>Processing: {uploadingMainFile.fileName}</p>
                  </>
                ) : (
                  <>
                    <p className="uploading-filename">Uploading: {uploadingMainFile.fileName}</p>
                    <div className="progress-bar-container">
                      <div className="progress-bar" style={{ width: `${uploadingMainFile.progress}%` }}></div>
                    </div>
                    <p className="upload-progress-text">{uploadingMainFile.progress}%</p>
                  </>
                )}
                {uploadingMainFile.error && <p className="upload-error-message">{uploadingMainFile.error}</p>}
              </div>
            ) : (
              <p className="no-image-text">No main image set.</p>
            )}
            {!mainImage && !isReadOnly && (
              <div className={`image-upload-area ${isDraggingOverMain ? 'dragging-over-active' : ''}`}>
                <input type="file" id="main-image-upload-input" onChange={(e) => handleFileSelect(e, true)} accept="image/*" />
                <label htmlFor="main-image-upload-input" className="file-input-label">
                  Click to select or drag & drop main image here.
                </label>
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        className={`photos-section property-images-display-section ${isDraggingOverAdditional ? 'dragging-over' : ''}`}
        onDragEnter={(e) => handleDragEnter(e, setIsDraggingOverAdditional)}
        onDragLeave={(e) => handleDragLeave(e, setIsDraggingOverAdditional)}
        onDragOver={handleDragOver}
        onDrop={(e) => handleDrop(e, setIsDraggingOverAdditional, false)}
      >
        <h4>Property Images</h4>
        {additionalImages.length > 0 || uploadingAdditionalFiles.length > 0 ? (
          <div className="image-list">
            {additionalImages.map((image, index) => (
              <div key={image.id} className="image-item">
                <img src={image.type === 'saved' ? getFullImageUrl(image.url) : image.url} alt={`Property view ${index + 1}`} className="image-thumbnail" />
                {!isReadOnly && (
                  <div className="image-actions">
                    <button onClick={() => handleSetMainClick(image)} className="set-main-image-btn" title="Set as Main Image">
                      <FontAwesomeIcon icon={faStar} />
                    </button>
                    {/* Rotation is now available for both saved and staged images */}
                    <button onClick={() => onImageRotate(image, 'left')} className="rotate-image-btn" title="Rotate Left">
                      <FontAwesomeIcon icon={faUndo} />
                    </button>
                    <button onClick={() => onImageRotate(image, 'right')} className="rotate-image-btn" title="Rotate Right">
                      <FontAwesomeIcon icon={faRedo} />
                    </button>
                    <button onClick={() => handleDeleteClick(image)} className="delete-image-btn" title={`Delete image ${index + 1}`}>
                      <FontAwesomeIcon icon={faTrashAlt} />
                    </button>
                  </div>
                )}
              </div>
            ))}
            {/* Display placeholders for additional images being uploaded */}
            {uploadingAdditionalFiles.map((file) => (
              <div key={file.tempId} className="image-item uploading-item">
                {file.status === 'processing' ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    <p className="uploading-filename" style={{ marginTop: '8px' }}>
                      Processing...
                    </p>
                  </>
                ) : (
                  <>
                    <p className="uploading-filename">{file.fileName}</p>
                    <div className="progress-bar-container">
                      <div className="progress-bar" style={{ width: `${file.progress}%` }}></div>
                    </div>
                    <p className="upload-progress-text">{file.progress}%</p>
                  </>
                )}
                {file.error && <p className="upload-error-message">{file.error}</p>}
              </div>
            ))}
          </div>
        ) : (
          <p>No additional property images uploaded.</p>
        )}
        {!isReadOnly && (
          <div className={`image-upload-area ${isDraggingOverAdditional ? 'dragging-over-active' : ''}`}>
            <input type="file" id="additional-images-upload-input" multiple onChange={(e) => handleFileSelect(e, false)} accept="image/*" />
            <label htmlFor="additional-images-upload-input" className="file-input-label">
              Click to select or drag & drop additional images here.
            </label>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageManager;
